import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Button } from '@kk/ui/components/Button/Button';
import { Icon } from '@kk/ui/components/Icon';
import { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuContent } from '@/components/SideNavigation/elements/MenuContent';
import type { SideNavigationLink } from '@/components/SideNavigation/SideNavigation';

type Props = {
  links?: SideNavigationLink[];
  menuOpen: boolean;
  onClose: () => void;
  onLogOut: () => void;
};

export function MobileMenu({
  links,
  menuOpen = false,
  onClose,
  onLogOut,
}: Props) {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const closeAndReset = () => {
    onClose();
  };

  return (
    <Transition show={menuOpen} as={Fragment}>
      <Dialog
        initialFocus={closeButtonRef}
        onClose={() => closeAndReset()}
        className="fixed inset-y-0 right-0 z-50 h-screen w-screen lg:hidden"
      >
        <TransitionChild as={Fragment}>
          <DialogBackdrop
            data-testid="leasing-mobile-menu-backdrop"
            className="bg-other-100/60 absolute inset-0 w-full transition-opacity duration-150 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in"
          />
        </TransitionChild>
        <TransitionChild as={Fragment}>
          <DialogPanel className="bg-blue-10 fixed inset-y-0 right-0 w-full overflow-hidden transition-transform duration-150 ease-in-out data-closed:translate-x-full sm:w-8/12">
            <TransitionChild as={Fragment}>
              <div className="fixed inset-0 transition-transform duration-150 ease-linear data-closed:-translate-x-full">
                <div className={'bg-teal-5 flex size-full flex-col'}>
                  <header
                    className={
                      'text-blue-60 flex h-22 items-center justify-between p-6'
                    }
                  >
                    <h2 className={'typ-headline-md text-neutral-200'}>Menu</h2>
                    <button
                      ref={closeButtonRef}
                      className="text-blue-60"
                      aria-label={t('closeMenu')}
                      onClick={closeAndReset}
                    >
                      <Icon
                        type="cross"
                        size={'medium'}
                        className="text-blue-60 size-6"
                        solid
                      />
                    </button>
                  </header>
                  <div className="flex h-full flex-col justify-between overflow-y-auto px-6">
                    <MenuContent links={links} />
                    <footer className="flex h-44 w-full flex-col pb-6 pt-10">
                      <div className={'flex h-fit w-full gap-4'}>
                        <Button
                          className="flex w-full justify-center"
                          theme="secondary"
                          onClick={onLogOut}
                        >
                          Log ud
                        </Button>
                      </div>
                      <a
                        href={
                          'https://www.kommunekredit.dk/leasing/om-leasing/leasing/'
                        }
                        className={
                          'typ-body text-blue-60 flex size-full items-center justify-center gap-2 py-4 font-bold no-underline'
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('burgerMenu.leasing')}
                        <Icon type={'link-external'} size="small" solid />
                      </a>
                    </footer>
                  </div>
                </div>
              </div>
            </TransitionChild>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

MobileMenu.displayName = 'MobileMenu';

export default MobileMenu;
